import { ColumnDef } from "@tanstack/react-table";
import { Link } from "react-router-dom";

import { StatusIndicator } from "components/partials/status-indicator/status-indicator";
import ApprovalQueueTableActionsMenu from "containers/admin/clients/client/approval-queue/components/approval-queue-table-actions-menu/approval-queue-table-actions-menu";

import { Route as AppRoute } from "utilities/app-routes";
import { formatDateTime, formatDueDate } from "utilities/date";

import { getBatchStatuses } from "containers/admin/clients/client/approval-queue/approval-queue.helpers";
import { EMPTY_DISPLAY_VALUE } from "constants/ui";

import {
  BatchItem,
  BasicTableActionsProps,
} from "containers/admin/clients/client/approval-queue/approval-queue.types";
import { NamedDictionaryItem } from "hooks/api/dictionary";

interface TableActionsProps extends BasicTableActionsProps {
  onView: (id: string) => void;
  onSendForReview: (id: string) => void;
  onDelete: (id: string) => void;
  batchStatuses: NamedDictionaryItem[];
}

const headerStyles = "text-gray-600 font-bold leading-5";

/**
 * A header stays on one line while the column covers its label plus 32px of cell padding, and 28px
 * more where the column sorts (the arrow and the gap before it).
 */
export const createApprovalQueueColumns = ({
  pathname,
  batchStatuses,
  onView,
  onSendForReview,
  onDelete,
}: TableActionsProps): ColumnDef<BatchItem>[] => [
  {
    id: "name",
    header: "Batch Name",
    accessorKey: "name",
    enableSorting: true,
    cell: ({ row }) => (
      <div className="flex items-center gap-3 pr-3">
        <Link
          to={`${pathname}/${AppRoute.batch}/${row.original.id}`}
          className="text-aqua-600 hover:text-aqua-700 font-semibold"
          aria-disabled={false}
          tabIndex={undefined}>
          {row.original.name}
        </Link>
      </div>
    ),
    size: 500,
    meta: {
      headerClassName: `${headerStyles} pl-4`,
      className: "py-1",
    },
  },
  {
    id: "progress",
    header: "Progress",
    accessorFn: (row) => {
      const { approvedCount = 0, totalCount = 0 } = row;

      return totalCount > 0 ? (approvedCount / totalCount) * 100 : 0;
    },
    enableSorting: false,
    cell: ({ row }) => {
      const { approvedCount, totalCount, status } = row.original;

      return (
        <span className="text-gray-600 font-medium">
          {status === "BUILDING" ? EMPTY_DISPLAY_VALUE : `${approvedCount}/${totalCount}`}
        </span>
      );
    },
    size: 100,
    meta: {
      headerClassName: `${headerStyles} pl-4`,
    },
  },
  {
    id: "status",
    header: "Status",
    accessorKey: "status",
    enableSorting: true,
    cell: ({ row }) => {
      const statuses = getBatchStatuses(batchStatuses, row.original.status);

      return <StatusIndicator versionStatuses={statuses} />;
    },
    size: 115,
    meta: {
      headerClassName: `${headerStyles} pl-4`,
    },
  },
  {
    id: "dueDate",
    header: "Due date",
    accessorKey: "dueDate",
    enableSorting: true,
    cell: ({ row }) => (
      <div className="font-medium text-gray-600">{formatDueDate(row.original.dueDate)}</div>
    ),
    size: 135,
    meta: {
      headerClassName: `${headerStyles} pl-4`,
    },
  },
  {
    id: "lastModifiedDate",
    header: "Last updated",
    accessorKey: "lastModifiedDate",
    enableSorting: true,
    cell: ({ row }) => (
      <div className="text-gray-600">{formatDateTime(row.original.lastModifiedDate)}</div>
    ),
    size: 160,
    meta: {
      headerClassName: `${headerStyles} pl-4`,
    },
  },
  {
    id: "action",
    header: "Action",
    enableSorting: false,
    cell: ({ row }) => (
      <ApprovalQueueTableActionsMenu
        item={{
          itemName: row.original.name,
          isSendToClientShown: !!row.original.totalCount && row.original.status === "BUILDING",
          itemId: row.original.id,
        }}
        {...{ onView, onSendForReview, onDelete }}
      />
    ),
    size: 56,
    meta: {
      headerClassName: headerStyles,
      className: "py-1 pr-8",
      isHeaderCentered: true,
    },
  },
];
